// src/pages/ApplicationChampionship/components/ChampionshipEditModal/index.tsx
import { useCallback, useEffect, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { usePutChampionshipMutation } from '@/services/queries/championship';
import { Championship } from '@/model';
import { PutChampionshipRequest } from '@/services/request/championship';

export interface ChampionshipEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: Championship;
  onClose: () => void;
  onDone?: () => void;
}

export const ChampionshipEditModal = ({ isOpen, onClose, item, onDone }: ChampionshipEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const putItemMutation = usePutChampionshipMutation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      name: item.name,
      is_filtered: item.is_filtered,
      is_active: item.is_active,
    },
    resolver: zodResolver(modalFormSchema),
  });

  useEffect(() => {
    reset({
      name: item.name,
      is_active: item.is_active ?? true,
      is_filtered: item.is_filtered ?? false,
    });
  }, [item, reset]);

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) return;

      const payload: PutChampionshipRequest = {
        name: data.name,
        is_filtered: data.is_filtered,
        is_active: data.is_active,
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_championship.components.championship_edit_modal.championship_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_championship.components.championship_edit_modal.championship_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_championship.components.championship_edit_modal.championship_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t('pages.application_championship.components.championship_edit_modal.championship_edit_name')}
              {...register('name')}
              helperText={t('pages.application_championship.components.championship_edit_modal.championship_edit_name_limits')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.name, errorMessage: errors?.name?.message }}
            />

            <FormControl display='flex' alignItems='center' justifyContent='space-between'>
              <Flex direction='column' gap={1}>
                <FormLabel mb={0}>
                  {t('pages.application_championship.components.championship_edit_modal.championship_edit_is_active_label')}
                </FormLabel>
                <FormHelperText>
                  {t('pages.application_championship.components.championship_edit_modal.championship_edit_is_active_helper')}
                </FormHelperText>
              </Flex>
              <Controller
                control={control}
                name='is_active'
                render={({ field }) => (
                  <Switch isChecked={field.value} onChange={e => field.onChange(e.target.checked)} colorScheme='green' />
                )}
              />
            </FormControl>

            <FormControl display='flex' alignItems='center' justifyContent='space-between'>
              <Flex direction='column' gap={1}>
                <FormLabel mb={0}>
                  {t('pages.application_championship.components.championship_edit_modal.championship_edit_is_filtered_label')}
                </FormLabel>
                <FormHelperText>
                  {t('pages.application_championship.components.championship_edit_modal.championship_edit_is_filtered_helper')}
                </FormHelperText>
              </Flex>
              <Controller
                control={control}
                name='is_filtered'
                render={({ field }) => (
                  <Switch isChecked={field.value} onChange={e => field.onChange(e.target.checked)} colorScheme='blue' />
                )}
              />
            </FormControl>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='space-between' gap='kilo'>
          <Flex w='100%' justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_championship.components.championship_edit_modal.championship_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_championship.components.championship_edit_modal.championship_edit_save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isPending} />
    </Modal>
  );
};
